/**
 * Sidebar stepper — 7 steps. Step 1 active in Phase 3; 2-7 locked.
 */
import React from "react";
import { motion } from "framer-motion";
import { Check, Lock } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../components/ui/tooltip";

export const STEPS = [
    { n: 1, label: "Hardware Setup", hint: "Vendor, model, hostname, DNS, timezone" },
    { n: 2, label: "Greenfield Options", hint: "Factory cleanup toggles" },
    { n: 3, label: "WAN & LACP", hint: "WAN interfaces + link aggregation" },
    { n: 4, label: "VLANs & Management", hint: "VLAN segmentation + mgmt network" },
    { n: 5, label: "Web Filter & Outbound", hint: "UTM profiles + outbound services" },
    { n: 6, label: "Smart Policies & L7", hint: "Smart policies + app reachability" },
    { n: 7, label: "Generate & Export", hint: "Build the final CLI / XML" },
];

const ZONE_TESTIDS = {
    1: "step-1", 2: "step-2", 3: "step-3", 4: "step-4",
    5: "step-5", 6: "step-6", 7: "step-7",
};

export const Stepper = ({ currentStep, completedSteps = new Set(), unlockedSteps, onSelect }) => {
    // If unlockedSteps is not provided (legacy callers), unlock current + completed only.
    const unlocked = unlockedSteps || new Set([currentStep, ...completedSteps]);
    return (
        <TooltipProvider delayDuration={300}>
            <nav
                className="flex flex-col gap-1.5 p-3 select-none"
                aria-label="Wizard steps"
                data-testid="wizard-stepper"
            >
                <p className="px-3 pt-2 pb-3 text-[10px] font-mono uppercase tracking-[0.32em] text-text-dim">
                    Steps
                </p>
                {STEPS.map((s) => {
                    const isActive = s.n === currentStep;
                    const isDone = completedSteps.has(s.n);
                    const isUnlocked = unlocked.has(s.n);
                    const isLocked = !isActive && !isUnlocked;

                    const inner = (
                        <button
                            type="button"
                            disabled={isLocked}
                            onClick={() => !isLocked && onSelect?.(s.n)}
                            data-testid={`stepper-${ZONE_TESTIDS[s.n]}`}
                            className={[
                                "group relative flex items-center gap-3 px-3 py-2.5 rounded-md w-full text-left transition-colors duration-200",
                                isActive && "bg-accent-cyan/10 border border-accent-cyan/30 shadow-glow-cyan",
                                isDone && "border border-accent-mint/30 hover:bg-accent-mint/5",
                                isLocked && "border border-white/[0.04] cursor-not-allowed",
                            ].filter(Boolean).join(" ")}
                        >
                            <span
                                className={[
                                    "inline-flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-mono shrink-0",
                                    isActive && "bg-accent-cyan text-canvas",
                                    isDone && "bg-accent-mint/20 text-accent-mint",
                                    isLocked && "bg-white/[0.03] text-text-dim",
                                ].filter(Boolean).join(" ")}
                            >
                                {isDone ? <Check className="w-3 h-3" /> : isLocked ? <Lock className="w-3 h-3" /> : s.n}
                            </span>
                            <span className="flex flex-col">
                                <span
                                    className={[
                                        "text-[13px] leading-tight",
                                        isActive ? "text-text-primary font-medium" : "text-text-muted",
                                    ].join(" ")}
                                >
                                    {s.label}
                                </span>
                                <span className="text-[10.5px] text-text-dim leading-tight mt-0.5">
                                    {s.hint}
                                </span>
                            </span>
                            {isActive && (
                                <motion.span
                                    layoutId="step-indicator"
                                    transition={{ type: "spring", stiffness: 380, damping: 30 }}
                                    className="absolute left-0 top-1.5 bottom-1.5 w-[3px] bg-accent-cyan rounded-r"
                                />
                            )}
                        </button>
                    );

                    if (isLocked) {
                        return (
                            <Tooltip key={s.n}>
                                <TooltipTrigger asChild>
                                    <div>{inner}</div>
                                </TooltipTrigger>
                                <TooltipContent side="right" className="bg-surface border-white/[0.08] text-text-primary">
                                    Complete previous steps first
                                </TooltipContent>
                            </Tooltip>
                        );
                    }
                    return <React.Fragment key={s.n}>{inner}</React.Fragment>;
                })}
            </nav>
        </TooltipProvider>
    );
};

export default Stepper;
